import { ref, type Ref } from "vue";
import { readPosReturnAttempt, savePosReturnAttempt, submitPosReturnAttempt } from "@/services/posReturnAttempt";
import { GetPosReturnCapabilities } from "@/api/posReturnsApi";
import { HttpClient, type HttpClientCallOptions } from "@/services/httpClient";
import { axiosInstance } from "@/plugins/axios";
import { resolvePosSaleEnvelope } from "@/services/posSaleEnvelope";
import { fetchFrappeDocCount, withFrappeLoading } from "@/services/frappe/list";
import { serializeFrappeFilters, type FrappeFilterClause } from "@/services/frappe/filters";
import { matchesRequestedUom } from "@/api/itemPriceUomMatch";
import { toBackendDiscountType } from "@/utils/posDiscount";
import { type Controllers } from "@/type/api";
import type {
  PosCatalogFilterParam,
  PosCatalogItem,
  PosCatalogResult,
  PosDiscountType,
  PosReceiptPayload,
  PosSalePayload,
  PosSaleResult,
} from "@/type/pos";

const CREATE_POS_SALE_METHOD = "/api/method/pet_app.api.pos.create_pos_sale";

const ITEM_DOCTYPE = "Item";
const ITEM_PRICE_DOCTYPE = "Item Price";
const BIN_DOCTYPE = "Bin";
const ITEM_BARCODE_DOCTYPE = "Item Barcode";
const ITEM_GROUP_DOCTYPE = "Item Group";
const PRICE_LIST_DOCTYPE = "Price List";
const DEFAULT_POS_CURRENCY = "IQD";

const itemService = new HttpClient(encodeURIComponent(ITEM_DOCTYPE) as Controllers);
const itemPriceService = new HttpClient(encodeURIComponent(ITEM_PRICE_DOCTYPE) as Controllers);
const binService = new HttpClient(encodeURIComponent(BIN_DOCTYPE) as Controllers);
const itemBarcodeService = new HttpClient(encodeURIComponent(ITEM_BARCODE_DOCTYPE) as Controllers);
const itemGroupService = new HttpClient(encodeURIComponent(ITEM_GROUP_DOCTYPE) as Controllers);
const priceListService = new HttpClient(encodeURIComponent(PRICE_LIST_DOCTYPE) as Controllers);

/**
 * The Frappe tag that marks a catalogue item as a till favourite. Tagged in the desk
 * ("fav" on the Item); read here from `_user_tags`, the standard tag column every
 * document carries, so no backend change is involved.
 */
export const POS_FAVOURITE_TAG = "fav";

/**
 * `_user_tags` is a comma-joined string with a leading comma (`",fav,new"`). Split it,
 * drop the empties, and compare exactly — a `like %fav%` query also matches "favorite",
 * so the query over-fetches on purpose and this is the real test.
 */
export function parseUserTags(raw: unknown): string[] {
  return String(raw ?? "")
    .split(",")
    .map(tag => tag.trim())
    .filter(Boolean);
}

const ITEM_FIELDS = JSON.stringify([
  "name",
  "item_code",
  "item_name",
  /**
   * The Arabic name Custom Field on Item — the same field the stock count and the item
   * definitions already read and search.
   *
   * ⚠️ POS WENT WITHOUT IT FOR A LONG TIME, and the symptom was silent: an Arabic till
   * rendered English product names even where a perfectly good Arabic one was stored,
   * because the field was simply never asked for. Requesting it is the whole fix.
   */
  "arabic_name",
  "item_group",
  "stock_uom",
  "description",
  "image",
  "disabled",
  "is_stock_item",
  "standard_rate",
  "modified",
  "_user_tags",
  /**
   * The site's real barcode column. `tabItem Barcode` is EMPTY here (confirmed by the
   * backend, 2026-09-26) and every code lives on this indexed Custom Field, which is why
   * `findExactItemByField("custom_barcode", …)` is the FIRST leg of a scan lookup.
   *
   * Requesting it on the list read is what lets a downloaded catalogue be scanned offline:
   * without it the device has item rows and no codes, and every scan misses — silently,
   * because a miss looks exactly like an unknown product.
   */
  "custom_barcode",
]);

const ITEM_PRICE_FIELDS = JSON.stringify([
  "name",
  "item_code",
  "price_list",
  "price_list_rate",
  "selling",
  // Without this the till cannot tell a price-per-single from a price-per-box and will
  // happily charge the latter for the former. See the ranking in `fetchPriceRowsForItems`.
  "uom",
  "modified",
]);

const BIN_FIELDS = JSON.stringify([
  "name",
  "warehouse",
  "item_code",
  "actual_qty",
]);

const ITEM_BARCODE_FIELDS = JSON.stringify([
  "name",
  "barcode",
  "parent",
  "parenttype",
]);

const ITEM_GROUP_FIELDS = JSON.stringify([
  "name",
  "item_group_name",
  "is_group",
]);

const PRICE_LIST_FIELDS = JSON.stringify([
  "name",
  "price_list_name",
  "currency",
  "enabled",
  "selling",
]);

interface ItemRow {
  name?: string | null;
  custom_barcode?: string | null;
  item_code?: string | null;
  item_name?: string | null;
  arabic_name?: string | null;
  item_group?: string | null;
  stock_uom?: string | null;
  description?: string | null;
  image?: string | null;
  disabled?: number | string | boolean | null;
  is_stock_item?: number | string | boolean | null;
  standard_rate?: number | string | null;
  _user_tags?: string | null;
}

interface ItemPriceRow {
  item_code?: string | null;
  price_list?: string | null;
  price_list_rate?: number | string | null;
  selling?: number | string | boolean | null;
  /** Blank on most rows. A NAMED unit restricts the row to that unit — see the ranking. */
  uom?: string | null;
  modified?: string | null;
}

interface BinRow {
  item_code?: string | null;
  actual_qty?: number | string | null;
}

interface ItemBarcodeRow {
  barcode?: string | null;
  parent?: string | null;
  parenttype?: string | null;
}

interface ItemGroupRow {
  name?: string | null;
  item_group_name?: string | null;
}

interface PriceListRow {
  name?: string | null;
  price_list_name?: string | null;
  currency?: string | null;
  enabled?: number | string | boolean | null;
  selling?: number | string | boolean | null;
}

function normalizeString(value: unknown) {
  const normalized = String(value ?? "").trim();
  return normalized || null;
}

function toNumber(value: unknown) {
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) ? numeric : 0;
}

function toBoolean(value: unknown) {
  if (typeof value === "boolean")
    return value;

  return Number(value ?? 0) === 1;
}

function toSortableTime(value: unknown) {
  const time = new Date(String(value ?? "")).getTime();
  return Number.isNaN(time) ? 0 : time;
}

function resolveItemCode(row: ItemRow) {
  return String(row.item_code ?? row.name ?? "").trim();
}

function buildItemFilters(filter: PosCatalogFilterParam): FrappeFilterClause[] {
  return [
    [ITEM_DOCTYPE, "disabled", "=", 0],
    ...(filter.itemGroup
      ? [[ITEM_DOCTYPE, "item_group", "=", filter.itemGroup] as FrappeFilterClause]
      : []),
    // Substring match on the tag column; callers confirm exact membership with
    // `parseUserTags` because `%fav%` also finds "favorite".
    ...(filter.tag
      ? [[ITEM_DOCTYPE, "_user_tags", "like", `%${filter.tag}%`] as FrappeFilterClause]
      : []),
  ];
}

function buildItemSearchFilters(search?: string | null): FrappeFilterClause[] {
  const normalizedSearch = search?.trim();
  if (!normalizedSearch)
    return [];

  return [
    [ITEM_DOCTYPE, "name", "like", `%${normalizedSearch}%`],
    [ITEM_DOCTYPE, "item_code", "like", `%${normalizedSearch}%`],
    [ITEM_DOCTYPE, "item_name", "like", `%${normalizedSearch}%`],
    // A cashier who can SEE the Arabic name must be able to TYPE it. Without this the
    // Arabic till shows a name that returns zero results when searched for.
    [ITEM_DOCTYPE, "arabic_name", "like", `%${normalizedSearch}%`],
    [ITEM_DOCTYPE, "item_group", "like", `%${normalizedSearch}%`],
  ];
}

/**
 * The one selling price per item that the till should charge.
 *
 * ⚠️ A ROW PRICED IN ANOTHER UNIT IS DROPPED, NOT DE-PRIORITISED.
 *
 * POS sells in the item's STOCK unit: the cart carries no unit and the invoice line sends
 * no `uom` or `conversion_factor`, so whatever rate this picks is charged for one stock
 * unit. The ranking used to be currency-match then `modified desc` with no unit test at
 * all — so once an item had a "Box of 12" price saved after its single-unit price, that
 * box row simply won, and the till charged one single at the price of twelve.
 *
 * A wrong-unit row is not a worse answer to "what does one single cost"; it answers a
 * different question, so it must be unreachable even as a last resort. A blank-`uom` row
 * still matches everything — see `matchesRequestedUom`, shared with the invoice path so
 * the till and the invoice can never resolve a unit differently.
 *
 * ⚠️ Filtered here rather than in the query on purpose: a Frappe `in [uom, null]` clause
 * does not reliably match NULL, and most rows in this catalogue have a blank unit — a
 * server-side filter would drop exactly the rows that should have matched, turning a
 * mispricing bug into a "no price found" bug.
 *
 * ⚠️ VISIBLE CONSEQUENCE: an item whose ONLY selling price names a non-stock unit now
 * falls through to `standard_rate` instead of showing that price. That is the fix working,
 * but it changes displayed prices on such items — audit them before deploying.
 */
async function fetchPriceRowsForItems(
  itemCodes: string[],
  preferredCurrency = DEFAULT_POS_CURRENCY,
  stockUomByItem = new Map<string, string | null>(),
  callOptions: HttpClientCallOptions = {},
) {
  const normalizedCodes = [...new Set(itemCodes.map(code => code.trim()).filter(Boolean))];
  if (!normalizedCodes.length)
    return new Map<string, ItemPriceRow>();
  const normalizedPreferredCurrency = normalizeString(preferredCurrency) || DEFAULT_POS_CURRENCY;

  const response = await itemPriceService.get<{ data?: ItemPriceRow[] }>(
    "",
    {
      filters: serializeFrappeFilters([
        [ITEM_PRICE_DOCTYPE, "item_code", "in", normalizedCodes],
        [ITEM_PRICE_DOCTYPE, "selling", "=", 1],
      ]),
      limit_start: 0,
      limit_page_length: Math.max(normalizedCodes.length * 8, 100),
      order_by: "modified desc",
    },
    {},
    ITEM_PRICE_FIELDS,
    callOptions,
  );
  const priceListRows = await fetchPriceListRows(
    (response?.data ?? [])
      .map(row => normalizeString(row.price_list))
      .filter((value): value is string => Boolean(value)),
    callOptions,
  ).catch(() => []);
  const currencyByPriceList = new Map(
    priceListRows
      .map(row => [String(row.name ?? "").trim(), normalizeString(row.currency)] as const)
      .filter(([priceList, currency]) => Boolean(priceList) && Boolean(currency)),
  );

  const rowsByItem = new Map<string, ItemPriceRow[]>();
  for (const row of response?.data ?? []) {
    const itemCode = String(row.item_code ?? "").trim();
    if (!itemCode)
      continue;

    rowsByItem.set(itemCode, [...(rowsByItem.get(itemCode) ?? []), row]);
  }

  return new Map(
    [...rowsByItem.entries()].map(([itemCode, rows]) => {
      const stockUom = stockUomByItem.get(itemCode) ?? null;
      const applicable = rows.filter(row => matchesRequestedUom(row, stockUom));

      return [
        itemCode,
        [...applicable].sort((left, right) => {
          // Unit outranks currency, which outranks recency. Wrong-unit rows are already
          // gone, so this only separates an explicit stock-unit row from a blank one — an
          // explicit match is a deliberate statement, a blank is an unfilled field.
          const normalizedStockUom = String(stockUom ?? "").trim();
          const leftExact = Boolean(normalizedStockUom) && String(left.uom ?? "").trim() === normalizedStockUom;
          const rightExact = Boolean(normalizedStockUom) && String(right.uom ?? "").trim() === normalizedStockUom;

          if (leftExact !== rightExact)
            return leftExact ? -1 : 1;

          const leftMatchesCurrency = currencyByPriceList.get(String(left.price_list ?? "").trim()) === normalizedPreferredCurrency;
          const rightMatchesCurrency = currencyByPriceList.get(String(right.price_list ?? "").trim()) === normalizedPreferredCurrency;

          if (leftMatchesCurrency !== rightMatchesCurrency)
            return leftMatchesCurrency ? -1 : 1;

          return toSortableTime(right.modified) - toSortableTime(left.modified);
        })[0],
      ] as const;
    }),
  );
}

async function fetchPriceListRows(priceListNames: string[], callOptions: HttpClientCallOptions = {}) {
  const normalizedNames = [...new Set(priceListNames.map(name => String(name).trim()).filter(Boolean))];
  if (!normalizedNames.length)
    return [] as PriceListRow[];

  const response = await priceListService.get<{ data?: PriceListRow[] }>(
    "",
    {
      filters: serializeFrappeFilters([
        [PRICE_LIST_DOCTYPE, "name", "in", normalizedNames],
      ]),
      limit_start: 0,
      limit_page_length: Math.max(normalizedNames.length, 20),
      order_by: "modified desc",
    },
    {},
    PRICE_LIST_FIELDS,
    callOptions,
  );

  return response?.data ?? [];
}

async function fetchStockRowsForItems(warehouse: string | null | undefined, itemCodes: string[]) {
  const normalizedWarehouse = normalizeString(warehouse);
  const normalizedCodes = [...new Set(itemCodes.map(code => code.trim()).filter(Boolean))];

  if (!normalizedWarehouse || !normalizedCodes.length)
    return new Map<string, BinRow>();

  const response = await binService.get<{ data?: BinRow[] }>(
    "",
    {
      filters: serializeFrappeFilters([
        [BIN_DOCTYPE, "warehouse", "=", normalizedWarehouse],
        [BIN_DOCTYPE, "item_code", "in", normalizedCodes],
      ]),
      limit_start: 0,
      limit_page_length: Math.max(normalizedCodes.length, 20),
      order_by: "modified desc",
    },
    {},
    BIN_FIELDS,
  );

  return new Map(
    (response?.data ?? [])
      .map(row => [String(row.item_code ?? "").trim(), row] as const)
      .filter(([itemCode]) => Boolean(itemCode)),
  );
}

function mapCatalogItem(
  row: ItemRow,
  priceRow: ItemPriceRow | null | undefined,
  priceListCurrency: string | null | undefined,
  binRow: BinRow | null | undefined,
  /**
   * The SCANNED string, on the barcode lookup path only. When absent the row falls back to
   * the item's own `custom_barcode`, so a grid row now carries its code too — which is what
   * the offline catalogue is built from.
   */
  barcode?: string | null,
): PosCatalogItem {
  const itemCode = resolveItemCode(row);
  const isStockItem = toBoolean(row.is_stock_item);
  const qtyAvailable = isStockItem ? toNumber(binRow?.actual_qty) : null;
  const priceFromList = Number(priceRow?.price_list_rate ?? NaN);
  const fallbackPrice = Number(row.standard_rate ?? 0);
  const price = Number.isFinite(priceFromList) && priceFromList > 0
    ? priceFromList
    : (Number.isFinite(fallbackPrice) ? fallbackPrice : 0);
  const tags = parseUserTags(row._user_tags);

  return {
    id: String(row.name ?? itemCode),
    itemCode,
    itemName: normalizeString(row.item_name) || itemCode,
    /**
     * ⚠️ NO FALLBACK TO `item_name`, DELIBERATELY. `null` means "this item has no Arabic
     * name", which is exactly what the display layer needs to know in order to fall back
     * for itself. Filling it from the English name here would make an unfilled catalogue
     * look complete. Same rule as `itemDefinitionApi`.
     */
    arabicName: normalizeString(row.arabic_name),
    itemGroup: normalizeString(row.item_group),
    stockUom: normalizeString(row.stock_uom),
    description: normalizeString(row.description),
    image: normalizeString(row.image),
    // The scanned string when there is one, else the item's own code, so a row read from
    // the grid can still be matched by a scanner offline.
    barcode: normalizeString(barcode) ?? normalizeString(row.custom_barcode),
    price,
    priceList: normalizeString(priceRow?.price_list),
    priceListCurrency: normalizeString(priceListCurrency) || DEFAULT_POS_CURRENCY,
    qtyAvailable,
    inStock: isStockItem ? Number(qtyAvailable ?? 0) > 0 : true,
    isStockItem,
    tags,
    isFavourite: tags.includes(POS_FAVOURITE_TAG),
  };
}

async function hydrateCatalogRows(
  rows: ItemRow[],
  warehouse?: string | null,
  barcodeByItemCode = new Map<string, string | null>(),
  callOptions: HttpClientCallOptions = {},
) {
  const itemCodes = rows.map(resolveItemCode);
  // The till prices one STOCK unit, so the price lookup has to know which unit that is —
  // otherwise a row priced per box can win and be charged for a single.
  const stockUomByItem = new Map(
    rows.map(row => [resolveItemCode(row), normalizeString(row.stock_uom)] as const),
  );
  const [priceMap, stockMap] = await Promise.all([
    fetchPriceRowsForItems(itemCodes, DEFAULT_POS_CURRENCY, stockUomByItem, callOptions),
    fetchStockRowsForItems(warehouse, itemCodes),
  ]);
  const priceListRows = await fetchPriceListRows(
    [...priceMap.values()]
      .map(row => normalizeString(row?.price_list))
      .filter((value): value is string => Boolean(value)),
    callOptions,
  ).catch(() => []);
  const currencyByPriceList = new Map(
    priceListRows
      .map(row => [String(row.name ?? "").trim(), normalizeString(row.currency)] as const)
      .filter(([name, currency]) => Boolean(name) && Boolean(currency)),
  );

  return rows.map((row) => {
    const itemCode = resolveItemCode(row);

    return mapCatalogItem(
      row,
      priceMap.get(itemCode),
      currencyByPriceList.get(String(priceMap.get(itemCode)?.price_list ?? "").trim()) ?? null,
      stockMap.get(itemCode),
      barcodeByItemCode.get(itemCode) ?? null,
    );
  });
}

export async function GetPosCatalogItems(
  loading: Ref<boolean>,
  filter: PosCatalogFilterParam = {},
  /**
   * `{ cache: "bypass" }` from the offline pack download: its whole job is fresh data,
   * and the price reads are cached (`Item Price` operational, `Price List` reference),
   * so a background refresh could otherwise store figures that were already stale.
   */
  callOptions: HttpClientCallOptions = {},
): Promise<PosCatalogResult | null> {
  return withFrappeLoading(loading, async () => {
    try {
      const filters = serializeFrappeFilters(buildItemFilters(filter));
      const orFilters = serializeFrappeFilters(buildItemSearchFilters(filter.search));

      const [response, total] = await Promise.all([
        itemService.get<{ data?: ItemRow[] }>(
          "",
          {
            limit_start: filter.limit_start ?? 0,
            limit_page_length: filter.limit_page_length ?? 24,
            order_by: filter.order_by ?? "modified desc",
            ...(filters ? { filters } : {}),
            ...(orFilters ? { or_filters: orFilters } : {}),
          },
          {},
          ITEM_FIELDS,
        ),
        fetchFrappeDocCount(itemService, ITEM_DOCTYPE, filters, orFilters).catch(() => 0),
      ]);

      const rows = response?.data ?? [];

      return {
        data: await hydrateCatalogRows(rows, filter.warehouse, undefined, callOptions),
        total,
      };
    } catch (error: any) {
      console.error(error?.message ?? error);
      return null;
    }
  });
}

export async function GetPosItemGroups(
  loading: Ref<boolean>,
  /** `{ cache: "bypass" }` from the pack download; `Item Group` is a 15-minute tier. */
  callOptions: HttpClientCallOptions = {},
): Promise<string[]> {
  return withFrappeLoading(loading, async () => {
    try {
      const response = await itemGroupService.get<{ data?: ItemGroupRow[] }>(
        "",
        {
          filters: serializeFrappeFilters([
            [ITEM_GROUP_DOCTYPE, "is_group", "=", 0],
          ]),
          limit_start: 0,
          limit_page_length: 300,
          order_by: "item_group_name asc",
        },
        {},
        ITEM_GROUP_FIELDS,
        callOptions,
      );

      return [...new Set(
        (response?.data ?? [])
          .map(row => normalizeString(row.item_group_name) || normalizeString(row.name))
          .filter((value): value is string => Boolean(value)),
      )];
    } catch (error: any) {
      console.error(error?.message ?? error);
      return [];
    }
  });
}

async function findExactItemByField(
  field: "custom_barcode" | "name" | "item_code",
  value: string,
  warehouse?: string | null,
  barcode?: string | null,
): Promise<PosCatalogItem | null> {
  const normalizedValue = normalizeString(value);
  if (!normalizedValue)
    return null;

  const response = await itemService.get<{ data?: ItemRow[] }>(
    "",
    {
      filters: serializeFrappeFilters([
        [ITEM_DOCTYPE, "disabled", "=", 0],
        [ITEM_DOCTYPE, field, "=", normalizedValue],
      ]),
      limit_start: 0,
      limit_page_length: 1,
      order_by: "modified desc",
    },
    {},
    ITEM_FIELDS,
  );

  const row = response?.data?.[0];
  if (!row)
    return null;

  return (await hydrateCatalogRows(
    [row],
    warehouse,
    new Map([[resolveItemCode(row), normalizeString(barcode)]]),
  ))[0] ?? null;
}

async function findItemCodeByBarcode(barcode: string) {
  const response = await itemBarcodeService.get<{ data?: ItemBarcodeRow[] }>(
    "",
    {
      filters: serializeFrappeFilters([
        [ITEM_BARCODE_DOCTYPE, "barcode", "=", barcode],
      ]),
      limit_start: 0,
      limit_page_length: 1,
      order_by: "modified desc",
    },
    {},
    ITEM_BARCODE_FIELDS,
  );

  const row = response?.data?.find(item =>
    !item.parenttype || String(item.parenttype) === ITEM_DOCTYPE,
  );

  return normalizeString(row?.parent);
}

/**
 * EVERY barcode in the system, as `barcode -> item_code`.
 *
 * The single-code lookup above is one request per scan, which is right online
 * and impossible offline. Offline mode downloads the whole map once (a few
 * thousand short rows) so a scan resolves from the device with no network.
 *
 * Paged because `Item Barcode` is the one table here that can outgrow the
 * catalogue: an item may carry several codes.
 */
export async function GetAllPosBarcodes(
  pageSize = 500,
  maxPages = 40,
): Promise<Record<string, string>> {
  const map: Record<string, string> = {};

  for (let page = 0; page < maxPages; page += 1) {
    const response = await itemBarcodeService.get<{ data?: ItemBarcodeRow[] }>(
      "",
      {
        limit_start: page * pageSize,
        limit_page_length: pageSize,
        order_by: "modified desc",
      },
      {},
      ITEM_BARCODE_FIELDS,
    );

    const rows = response?.data ?? [];

    for (const row of rows) {
      // Same parent rule as the single lookup: a barcode row can hang off another
      // doctype, and treating that as an item would resolve a scan to nothing.
      if (row.parenttype && String(row.parenttype) !== ITEM_DOCTYPE)
        continue;

      const barcode = normalizeString(row.barcode);
      const itemCode = normalizeString(row.parent);

      if (barcode && itemCode)
        map[barcode] = itemCode;
    }

    if (rows.length < pageSize)
      break;
  }

  return map;
}

export async function FindPosItemByBarcode(
  barcode: string,
  warehouse?: string | null,
): Promise<PosCatalogItem | null> {
  const normalizedBarcode = normalizeString(barcode);
  if (!normalizedBarcode)
    return null;

  // ⚠️ The site's own single-barcode field, which the barcode generator mints into. The
  // till used to skip it entirely, so a label the Stock Checker resolved came back "not
  // found" here. Same leg order as `FindStockCountItemByBarcode`.
  const directItem = await findExactItemByField("custom_barcode", normalizedBarcode, warehouse, normalizedBarcode);
  if (directItem)
    return directItem;

  const itemCodeFromBarcode = await findItemCodeByBarcode(normalizedBarcode).catch(() => null);

  if (itemCodeFromBarcode) {
    const barcodeItem = await findExactItemByField("name", itemCodeFromBarcode, warehouse, normalizedBarcode)
      || await findExactItemByField("item_code", itemCodeFromBarcode, warehouse, normalizedBarcode);

    if (barcodeItem)
      return barcodeItem;
  }

  return await findExactItemByField("name", normalizedBarcode, warehouse, normalizedBarcode)
    || await findExactItemByField("item_code", normalizedBarcode, warehouse, normalizedBarcode);
}

function calculateDiscountAmount(
  subtotalAmount: number,
  discountType?: PosDiscountType | null,
  discountValue?: number | null,
  explicitDiscountAmount?: number | null,
) {
  if (Number(explicitDiscountAmount ?? 0) > 0)
    return Math.min(Number(explicitDiscountAmount ?? 0), subtotalAmount);

  const value = Number(discountValue ?? 0);
  if (value <= 0)
    return 0;

  if (discountType === "Percentage")
    return Math.min(subtotalAmount * (Math.min(value, 100) / 100), subtotalAmount);

  return Math.min(value, subtotalAmount);
}

function buildDiscountLabel(type: PosDiscountType | null | undefined, value: number | null | undefined) {
  const numericValue = Number(value ?? 0);
  if (numericValue <= 0)
    return null;

  return type === "Percentage"
    ? `${numericValue}%`
    : null;
}

/*
 * ⚠️ REMOVED WITH THE MOVE TO `pet_app.api.pos.create_pos_sale`:
 *   resolveCashierProfileCashAccount, resolveSalePriceListContext,
 *   resolvePosProfilePriceListContext, fetchDefaultSellingPriceList.
 *
 * The server now reads the price list, currency and cash account from the POS
 * Profile. Keeping client-side resolvers would be worse than dead code: the cash
 * account in particular MUST come from the server, which re-asserts the till's own
 * account between the last draft save and submit — the fix for the Hotel till's
 * takings landing in the Store's drawer. Anything here that looked
 * authoritative would invite someone to start sending it again.
 */
/** The till's own clock as `HH:MM`, for the receipt's Time row. */
function currentClockTime() {
  const now = new Date();

  return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
}

function buildReceipt(payload: PosSalePayload, invoiceId: string): PosReceiptPayload {
  const paymentStatus = payload.paymentStatus === "Due" ? "Due" : "Paid";
  const items = payload.items.map(item => ({
    label: item.label,
    qty: Number(item.qty ?? 0),
    unitPrice: Number(item.unitPrice ?? 0),
    amount: Number(item.qty ?? 0) * Number(item.unitPrice ?? 0),
  }));

  const subtotalAmount = items.reduce((sum, item) => sum + item.amount, 0);
  const discountAmount = calculateDiscountAmount(
    subtotalAmount,
    payload.discountType,
    payload.discountValue,
    payload.discountAmount,
  );
  const totalAmount = Math.max(subtotalAmount - discountAmount, 0);
  const paidAmount = paymentStatus === "Due" ? 0 : Number(payload.paidAmount ?? 0);

  return {
    invoiceId,
    postingDate: payload.postingDate,
    // This receipt is built as the sale is made, so the till clock IS the sale time.
    // Backend-returned receipts carry their own `posting_time` instead (posSaleEnvelope).
    postingTime: currentClockTime(),
    customerLabel: payload.customerLabel,
    cashierProfileName: payload.cashierProfileName,
    paymentStatus,
    paymentMode: paymentStatus === "Due" ? null : payload.paymentMode ?? null,
    paidAmount,
    changeAmount: paymentStatus === "Due" ? 0 : Math.max(paidAmount - totalAmount, 0),
    balanceDueAmount: paymentStatus === "Due" ? totalAmount : Math.max(totalAmount - paidAmount, 0),
    subtotalAmount,
    discountAmount,
    discountLabel: buildDiscountLabel(payload.discountType, payload.discountValue),
    totalAmount,
    // The partner and its order number reach the printed receipt, so a slip handed
    // to a courier can be matched to the app's own order without a lookup.
    partnerLabel: payload.deliveryPartner ?? null,
    partnerOrderRef: payload.partnerOrderRef ?? null,
    items,
  };
}

/**
 * ⚠️ THE VOCABULARY DIFFERS FROM OURS. This app says `"Fixed"`; the backend's
 * `discount_type` takes `"Amount"`. Sent unmapped it is an unrecognised type and
 * the discount silently does not apply. `"Percentage"` matches on both sides.
 *
 * The translation itself now lives in `toBackendDiscountType`, because this copy was
 * the only one for a long time: the driver-order and settlement doors send the same
 * field and sent it raw, which refused one and silently dropped the other.
 *
 * Only `discount_value` crosses the wire — the server computes the amount, so
 * the client no longer decides what comes off the total.
 */
function buildSaleDiscountFields(payload: PosSalePayload) {
  const value = Number(payload.discountValue ?? 0);

  if (!payload.discountType || value <= 0)
    return {};

  return {
    discount_type: toBackendDiscountType(payload.discountType),
    discount_value: value,
  };
}

/**
 * The delivery-partner fields, or nothing at all.
 *
 * ⚠️ WHAT IS SENT IS THE PARTNER'S TERMS, NOT OUR ARITHMETIC. Same discipline as
 * the discount above: the client states the rate — and, for a flat-fee partner, the
 * fee per order — that applied, and the server works out what the partner keeps. The
 * cart's commission preview is never sent; a client-computed total would be a second
 * opinion about money, and the two would drift the first time rounding differed.
 *
 * ⚠️ THE TYPE IS NOT SENT EITHER. It belongs to the partner record, so the server
 * reads it there and ignores whichever of the two figures does not apply. There is no
 * type selector at the till, by design.
 *
 * ⚠️ NOTHING HERE REDUCES THE INVOICE. The customer paid the app the full price;
 * the commission is settled separately against the partner's account.
 */
function buildPartnerFields(payload: PosSalePayload) {
  const partner = String(payload.deliveryPartner ?? "").trim();

  if (!partner)
    return {};

  return {
    delivery_partner: partner,
    partner_order_ref: String(payload.partnerOrderRef ?? "").trim() || undefined,
    partner_customer_name: String(payload.partnerCustomerName ?? "").trim() || undefined,
    // Both snapshots go, always. The server reads whichever one the PARTNER's own
    // commission type calls for and ignores the other — dropping the rate for a
    // flat-fee partner would change nothing there and would be a behaviour change
    // for percentage partners for no gain.
    partner_commission_rate: payload.partnerCommissionRate ?? undefined,
    partner_commission_amount: payload.partnerCommissionAmount ?? undefined,
  };
}

/**
 * Create and submit one POS sale.
 *
 * ⚠️ ONE CALL, NOT AN INSERT-THEN-SUBMIT. `sales_invoice_guard.before_insert`
 * refuses every direct `frappe.client.insert` of a Sales Invoice — deliberately,
 * because that path is what produced "ten invoices in sixty-eight minutes". POS
 * goes through a named door that creates and submits inside ONE transaction, so
 * a refusal rolls the whole thing back and can no longer strand a draft the way
 * the old two-step could.
 *
 * ⚠️ THE CLIENT NO LONGER RESOLVES THE PRICE LIST, CURRENCY OR CASH ACCOUNT.
 * The server reads them from the POS Profile. That drops two round trips before
 * every sale — and the cash account in particular MUST come from the server: it
 * re-asserts the till's own account between the last draft save and submit,
 * because ERPNext's `set_account_for_mode_of_payment` was silently rewriting the
 * Hotel till's takings into the Store's drawer. A client-derived account fights
 * that fix.
 *
 * Contract: `docs/backend/pos-sale-creation.md`.
 */
export async function CreatePosSale(
  loading: Ref<boolean>,
  payload: PosSalePayload,
): Promise<PosSaleResult | null> {
  return withFrappeLoading(loading, async () => {
    if (payload.returnCredit) {
      if (payload.paymentStatus === "Due" || payload.deliveryPartner || payload.prizeClaimCode
        || !Number.isFinite(payload.returnCredit.amount) || payload.returnCredit.amount <= 0)
        throw new Error("alkokh.posReturns.invalidCredit");
      const capabilities = await GetPosReturnCapabilities(payload.cashierProfileName);
      if (capabilities.version !== 1 || !capabilities.credit_sale)
        throw new Error("alkokh.posReturns.unavailable");
      const body = { sale: payload };
      const saved = readPosReturnAttempt(payload.cashierProfileName);
      if (saved && (saved.kind !== "credit_sale" || JSON.stringify(saved.body) !== JSON.stringify(body)))
        throw new Error("alkokh.posReturns.pending");
      const attempt = saved ?? savePosReturnAttempt(payload.cashierProfileName, "credit_sale", body);
      return await submitPosReturnAttempt<PosSaleResult>(payload.cashierProfileName, attempt);
    }
    const isDueSale = payload.paymentStatus === "Due";

    const response = await axiosInstance.post(CREATE_POS_SALE_METHOD, {
      customer: payload.customerId,
      /**
       * ⚠️ SENT ON A DUE SALE TOO, WHERE IT USED TO BE OMITTED.
       *
       * A Due sale is `is_pos = 0`, so ERPNext's own POS fields do not apply —
       * which is why this was left out, and why the guard could not simply
       * exempt POS: on the wire a Due sale looked identical to any other direct
       * insert. The server needs it regardless, because branch, warehouse, price
       * list and cashier attribution all come from the profile, and a Due sale
       * that cannot say which counter made it is not reportable.
       */
      pos_profile: payload.cashierProfileName,
      payment_status: isDueSale ? "Due" : "Paid",
      company: payload.company || undefined,
      warehouse: payload.warehouse || undefined,
      posting_date: payload.postingDate,
      due_date: payload.postingDate,
      // ⚠️ No money on a Due sale — the backend refuses it outright
      // (`DUE_SALE_CANNOT_CARRY_PAYMENT`).
      paid_amount: isDueSale ? undefined : Number(payload.paidAmount ?? 0),
      payment_mode: isDueSale ? undefined : payload.paymentMode || undefined,
      ...buildSaleDiscountFields(payload),
      /**
       * ⚠️ OMITTED ENTIRELY ON AN ORDINARY SALE, so a counter sale's payload is
       * byte-identical to what it was before delivery partners existed. A backend
       * that has not deployed the partner fields yet sees exactly the request it
       * always saw, and only a partner order carries anything new.
       */
      ...buildPartnerFields(payload),
      /**
       * ⚠️ OMITTED ENTIRELY WITHOUT A PRIZE, so an ordinary sale's request is unchanged. The
       * server redeems the code in the same transaction as the invoice and re-checks the
       * discount against the prize (docs/backend/pos-game-prizes.md §4).
       */
      ...(payload.prizeClaimCode ? { prize_claim_code: payload.prizeClaimCode } : {}),
      // The transporter's delivery job, booked with the invoice; absent on an ordinary sale.
      ...(payload.transportJob ? { transport_job: payload.transportJob } : {}),
      remarks: payload.remarks || undefined,
      idempotency_key: payload.idempotencyKey || undefined,
      /**
       * ⚠️ OMITTED ENTIRELY ON AN ONLINE SALE, so its request is byte-identical to what it
       * was before offline mode. A site that has not run `pet_app.patches.offline_pos_sale`
       * refuses a sale that DOES carry them (`OFFLINE_SALE_NOT_INSTALLED`) rather than
       * silently dropping the receipt number — which is the duplicate guard.
       */
      receipt_no: payload.receiptNo || undefined,
      offline_created_at: payload.offlineCreatedAt || undefined,
      device_id: payload.deviceId || undefined,
      items: payload.items.map(item => ({
        item_code: item.itemCode,
        item_name: item.label,
        qty: Number(item.qty ?? 0),
        rate: Number(item.unitPrice ?? 0),
        // No `amount`: a line is worth qty x rate as ERPNext computes it, and a
        // client-side total must never decide what the customer is charged.
        //
        // No `uom` / `conversion_factor` either. The cart is stock-unit-only by design,
        // and the backend already defaults to exactly that — sending it would change the
        // payload every till emits for zero change in behaviour. This is why
        // `fetchPriceRowsForItems` must refuse a price row priced in any other unit:
        // nothing downstream of here can tell that the rate was meant for a box.
        // Letting POS SELL in a chosen unit is separate work.
      })),
    });

    const raw = response?.data?.message ?? response?.data ?? null;
    if (!raw)
      return null;

    // No fallback invoice id: this path creates a NEW invoice, so if the backend
    // named none there is nothing to fall back to and guessing would be worse.
    const envelope = await resolvePosSaleEnvelope(raw, "", payload.items.length);

    return {
      invoice: envelope.invoice,
      paymentEntryId: envelope.paymentEntryId,
      // The server's receipt when it sent one; otherwise built from the payload
      // exactly as before, so a missing receipt never stops the sale printing.
      receipt: envelope.receipt ?? buildReceipt(payload, envelope.invoice.id),
      replayed: envelope.replayed,
    };
  });
}
